import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError, apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import type {
  CashMovement,
  CashRegister,
  CounterSale,
  Sale,
  SellableCatalog,
  SessionReport,
} from './types'

/**
 * Le poste de vente du soir J.
 *
 * Comme toutes les surfaces `/ops`, chaque appel porte le jeton **et**
 * l'en-tête d'édition : un rôle attribué sur une édition n'accorde rien sans
 * lui, et l'oubli produit un 403 qui envoie chercher du côté des permissions.
 */
async function authorized(eventId: string | undefined) {
  return { token: await accessToken(), ...(eventId ? { eventId } : {}) }
}

export function useRegisters(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['pos-registers', eventId],
    queryFn: async () => apiClient.get<CashRegister[]>('/ops/pos/registers', await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
  })
}

/**
 * La caisse que je tiens, ou `null`.
 *
 * `null` n'est pas une erreur : c'est l'état normal avant la prise de poste.
 * Rafraîchi régulièrement — un responsable peut clôturer une caisse à distance.
 */
export function useMySession(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['pos-session', eventId],
    queryFn: async () =>
      apiClient.get<SessionReport | null>('/ops/pos/session', await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 60_000,
    // Un 403 est une réponse (ce rôle ne tient pas de caisse), pas une panne
    // passagère : le réessayer retardait d'autant l'écran qui le dit.
    retry: (count, error) => !(error instanceof ApiError && error.status === 403) && count < 1,
  })
}

export function useCatalogue(eventId: string | undefined, enabled: boolean) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['pos-catalogue', eventId, i18n.language],
    queryFn: async () =>
      apiClient.get<SellableCatalog>(
        `/ops/pos/catalogue?locale=${i18n.language}`,
        await authorized(eventId),
      ),
    enabled: Boolean(eventId) && enabled,
  })
}

export function useOpenSession(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { register_id: string; opening_float_minor: number }) =>
      apiClient.post<SessionReport>('/ops/pos/session/open', payload, await authorized(eventId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
      // Le catalogue dépend de la caisse ouverte : un bar ne propose pas de
      // plats, et le filtre vient du poste, pas d'un choix du caissier.
      void queryClient.invalidateQueries({ queryKey: ['pos-catalogue', eventId] })
    },
  })
}

export function useCloseSession(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { counted_minor: number; note: string | null }) =>
      apiClient.post<SessionReport>('/ops/pos/session/close', payload, await authorized(eventId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-registers', eventId] })
    },
  })
}

/**
 * Les mouvements du tiroir que je tiens, le plus récent en tête.
 *
 * C'est ce qu'on relit quand le théorique surprend : chaque vente en espèces,
 * chaque sortie, chaque apport, avec son motif.
 */
export function useMyMovements(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['pos-movements', eventId],
    queryFn: async () =>
      apiClient.get<CashMovement[]>('/ops/pos/session/movements', await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
  })
}

/**
 * Un apport, une sortie ou une correction.
 *
 * Le sens d'un apport ou d'une sortie vient du geste, pas du signe tapé : le
 * serveur le rétablit. Seule la correction garde le signe saisi.
 */
export function useAddMovement(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      kind: 'deposit' | 'payout' | 'correction'
      amount_minor: number
      note: string | null
    }) =>
      apiClient.post<CashMovement>('/ops/pos/session/movements', payload, await authorized(eventId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-movements', eventId] })
    },
  })
}

/**
 * Mes ventes au comptoir de ce service, la plus récente en tête.
 *
 * L'annulation ne vivait que sur le ticket à l'écran : la vente suivante
 * l'effaçait, et l'erreur de la précédente n'avait plus de bouton.
 */
export function useMySales(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['pos-sales', eventId],
    queryFn: async () =>
      apiClient.get<CounterSale[]>('/ops/pos/session/sales', await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
  })
}

export function useSell(eventId: string | undefined) {
  const queryClient = useQueryClient()
  const { i18n } = useTranslation()

  return useMutation({
    mutationFn: async (payload: {
      lines: { kind: string; ref_id: string; quantity: number }[]
      payment_method: string
      tendered_minor: number | null
      note: string | null
      /** Facultatif : le prénom qu'on appellera quand c'est prêt. */
      customer_label: string | null
    }) =>
      apiClient.post<Sale>(
        `/ops/pos/sales?locale=${i18n.language}`,
        payload,
        await authorized(eventId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-movements', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-sales', eventId] })
      // Le stock a bougé : une carte épuisée doit se griser tout de suite.
      void queryClient.invalidateQueries({ queryKey: ['pos-catalogue', eventId] })
    },
  })
}


/**
 * Annuler la vente qu on vient d encaisser.
 *
 * Le stock revient, le reglement cesse de compter — le chiffre d affaires se
 * lit sur les paiements — et le tiroir est corrige si c etait des especes.
 */
export function useVoidSale(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { orderId: string; reason: string }) =>
      apiClient.post<{ order_id: string; order_number: string; status: string }>(
        `/ops/pos/sales/${payload.orderId}/void`,
        { reason: payload.reason },
        await authorized(eventId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-catalogue', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-movements', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-sales', eventId] })
    },
  })
}
